<script lang="ts">
  import { link } from 'svelte-spa-router';
  import { bookmarks, bookmarksOpen } from '$stores/ui.js';
  import { currentPath } from '$stores/content.js';

  function close() {
    bookmarksOpen.set(false);
  }

  function togglePin(path: string, isPinned: boolean) {
    if (isPinned) {
      bookmarks.unpin(path);
    } else {
      bookmarks.pin(path);
    }
  }

  function remove(path: string) {
    bookmarks.remove(path);
  }

  $: pinnedBookmarks = $bookmarks.filter((b) => b.pinned);
  $: recentBookmarks = $bookmarks.filter((b) => !b.pinned);
</script>

<aside class="bookmarks" class:open={$bookmarksOpen}>
  <div class="bookmarks-header">
    <h3>{__STRINGS__.bookmarks.heading}</h3>
    <button class="close-btn" on:click={close} aria-label={__STRINGS__.bookmarks.closeLabel}>
      &times;
    </button>
  </div>

  <div class="bookmarks-content">
    {#if pinnedBookmarks.length > 0}
      <section class="bookmark-section">
        <h4>{__STRINGS__.bookmarks.pinned}</h4>
        <ul>
          {#each pinnedBookmarks as bookmark}
            <li class:active={bookmark.path === $currentPath || bookmark.path.split('#')[0] === $currentPath}>
              <a href="/{bookmark.path}" use:link class="bookmark-link">
                {bookmark.title}
              </a>
              <div class="bookmark-actions">
                <button
                  class="action-btn"
                  on:click={() => togglePin(bookmark.path, bookmark.pinned)}
                  aria-label={__STRINGS__.bookmarks.unpin}
                  title={__STRINGS__.bookmarks.unpin}
                >
                  ★
                </button>
                <button
                  class="action-btn"
                  on:click={() => remove(bookmark.path)}
                  aria-label={__STRINGS__.bookmarks.remove}
                  title={__STRINGS__.bookmarks.remove}
                >
                  ×
                </button>
              </div>
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    {#if recentBookmarks.length > 0}
      <section class="bookmark-section">
        <h4>{__STRINGS__.bookmarks.recent}</h4>
        <ul>
          {#each recentBookmarks as bookmark}
            <li class:active={bookmark.path === $currentPath || bookmark.path.split('#')[0] === $currentPath}>
              <a href="/{bookmark.path}" use:link class="bookmark-link">
                {bookmark.title}
              </a>
              <div class="bookmark-actions">
                <button
                  class="action-btn"
                  on:click={() => togglePin(bookmark.path, bookmark.pinned)}
                  aria-label={__STRINGS__.bookmarks.pin}
                  title={__STRINGS__.bookmarks.pin}
                >
                  ☆
                </button>
                <button
                  class="action-btn"
                  on:click={() => remove(bookmark.path)}
                  aria-label={__STRINGS__.bookmarks.remove}
                  title={__STRINGS__.bookmarks.remove}
                >
                  ×
                </button>
              </div>
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    {#if $bookmarks.length === 0}
      <p class="empty">{__STRINGS__.bookmarks.empty}</p>
    {/if}
  </div>
</aside>

<style>
  .bookmarks {
    width: 250px;
    height: calc(100vh - 56px);
    background: var(--color-surface, #fff);
    border-left: 1px solid var(--color-border, #e0e0e0);
    overflow-y: auto;
    flex-shrink: 0;
    position: fixed;
    top: 56px;
    right: -250px;
    z-index: 1;
    transition: right 0.2s ease-out;
  }

  .bookmarks.open {
    right: 0;
  }

  .bookmarks-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid var(--color-border, #e0e0e0);
  }

  .bookmarks-header h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-heading, #1a1a2e);
  }

  .close-btn {
    display: none;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: var(--color-text-muted, #666);
  }

  .bookmarks-content {
    padding: 0.5rem;
  }

  .bookmark-section h4 {
    margin: 0.75rem 0 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.65rem;
    font-weight: 700;
    color: var(--color-text-muted, #999);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    border-bottom: 1px solid var(--color-border, #e0e0e0);
  }

  .bookmark-section + .bookmark-section h4 {
    margin-top: 1rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    display: flex;
    align-items: center;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
  }

  li:hover {
    background: var(--color-surface-hover, #f5f5f5);
  }

  li.active {
    background: var(--color-surface-hover, #f5f5f5);
  }

  li.active .bookmark-link {
    color: var(--color-primary, #e94560);
    font-weight: 500;
  }

  .bookmark-link {
    flex: 1;
    color: var(--color-text, #333);
    text-decoration: none;
    font-size: 0.9rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .bookmark-actions {
    display: flex;
    gap: 2px;
    opacity: 0;
    transition: opacity 0.15s;
  }

  li:hover .bookmark-actions {
    opacity: 1;
  }

  .action-btn {
    background: none;
    border: none;
    padding: 2px 4px;
    cursor: pointer;
    color: var(--color-text-muted, #666);
    font-size: 0.9rem;
  }

  .action-btn:hover {
    color: var(--color-primary, #e94560);
  }

  .empty {
    padding: 1rem;
    color: var(--color-text-muted, #666);
    font-size: 0.9rem;
    text-align: center;
  }

  /* Match the overlay's breakpoint so the panel is always above the dim backdrop */
  @media (max-width: 1200px) {
    .bookmarks {
      z-index: 99;
    }

    .close-btn {
      display: block;
    }
  }
</style>
